import React from "react";
import { Skeleton } from "antd";

/**
 * Skeleton placeholders for the app shell (sidebar + navbar) shown while the
 * authenticated user / organization context is still hydrating. They mirror the
 * real DOM structure and class names so the layout doesn't shift when the real
 * content swaps in.
 */

/** Deterministic "random-ish" label widths so rows look organic without
 *  Math.random (which would reshuffle on every render). */
const labelWidth = (i: number): string => `${55 + ((i * 17) % 35)}%`;

/** Sidebar navigation skeleton - a few sections of icon + label rows. */
export const SidebarNavSkeleton: React.FC<{ collapsed?: boolean }> = ({
    collapsed = false,
}) => {
    const sections = [4, 5, 3];
    return (
        <div
            className="sidebar-nav__inner sidebar-nav__inner--skeleton"
            aria-hidden
        >
            {sections.map((count, s) => (
                <div key={s} className="nav-section">
                    {!collapsed && (
                        <div className="nav-section__title">
                            <Skeleton.Input
                                active
                                size="small"
                                style={{ width: 72, minWidth: 72, height: 10 }}
                            />
                        </div>
                    )}
                    {Array.from({ length: count }).map((_, i) => (
                        <div
                            key={i}
                            className="nav-item nav-item--skeleton"
                            style={{ cursor: "default" }}
                        >
                            <div className="nav-item__icon">
                                <Skeleton.Avatar
                                    active
                                    size={24}
                                    shape="square"
                                />
                            </div>
                            {!collapsed && (
                                <span
                                    className="nav-item__label"
                                    style={{ flex: 1 }}
                                >
                                    <Skeleton.Input
                                        active
                                        size="small"
                                        style={{
                                            width: labelWidth(i),
                                            minWidth: 64,
                                            height: 12,
                                        }}
                                    />
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

/** Navbar org / category breadcrumb skeleton - keeps the labels, swaps the
 *  selects for skeleton inputs. */
export const NavContextSkeleton: React.FC<{ showCategory?: boolean }> = ({
    showCategory = true,
}) => (
    <div className="nav-context" aria-hidden>
        <div className="nav-context__chunk nav-context__chunk--org">
            <span className="nav-context__icon" aria-hidden>
                <Skeleton.Avatar active size={20} shape="square" />
            </span>
            <div className="nav-context__body">
                <span className="nav-context__label">Organization</span>
                <Skeleton.Input
                    active
                    size="small"
                    style={{ width: 150, minWidth: 120, height: 16 }}
                />
            </div>
        </div>

        {showCategory && (
            <>
                <span className="nav-context__separator" aria-hidden>
                    ⁄
                </span>
                <div className="nav-context__chunk nav-context__chunk--category">
                    <span
                        className="nav-context__icon nav-context__icon--muted"
                        aria-hidden
                    >
                        <Skeleton.Avatar active size={20} shape="square" />
                    </span>
                    <div className="nav-context__body">
                        <span className="nav-context__label">Category</span>
                        <Skeleton.Input
                            active
                            size="small"
                            style={{ width: 120, minWidth: 96, height: 16 }}
                        />
                    </div>
                </div>
            </>
        )}
    </div>
);

/** A single circular icon-button placeholder (notification bell, etc.). */
export const NavIconSkeleton: React.FC = () => (
    <span
        className="nav__icon-btn"
        aria-hidden
        style={{ pointerEvents: "none" }}
    >
        <Skeleton.Avatar active size={20} shape="circle" />
    </span>
);

/** Profile avatar placeholder for the navbar right cluster. */
export const NavProfileSkeleton: React.FC = () => (
    <span aria-hidden style={{ display: "inline-flex", alignItems: "center" }}>
        <Skeleton.Avatar active size={32} shape="circle" />
    </span>
);
